import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { signIn, kidSignIn } from '../../api';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiLock, FiArrowRight, FiLoader, FiMail, FiShield } = FiIcons;

function Login() {
  const [loginMode, setLoginMode] = useState('kid'); // 'kid' or 'parent'
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { dispatch } = useGame();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (loginMode === 'kid') {
        const kid = await kidSignIn(identifier, password);
        dispatch({ type: 'KID_AUTH_SUCCESS', payload: kid });
        navigate('/dashboard');
      } else {
        const parent = await signIn(identifier, password);
        dispatch({ type: 'AUTH_SUCCESS', payload: parent });
        navigate('/parent');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-jungle-100 to-safari-100"
    >
      <div className="max-w-md w-full bg-white rounded-[3rem] p-10 shadow-2xl border-4 border-safari-200">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">{loginMode === 'kid' ? '🦁' : '🔑'}</div>
          <h1 className="text-3xl font-kid text-jungle-700">
            {loginMode === 'kid' ? 'Explorer Login' : 'Parent Portal'}
          </h1>
          <p className="font-comic text-jungle-600">
            {loginMode === 'kid' ? 'Ready for an adventure?' : 'Manage your explorers'}
          </p>
        </div>

        {/* Mode Toggle */}
        <div className="flex bg-jungle-50 p-1 rounded-2xl mb-8 border-2 border-jungle-100">
          <button
            onClick={() => { setLoginMode('kid'); setIdentifier(''); setPassword(''); }}
            className={`flex-1 py-3 rounded-xl font-kid transition-all flex items-center justify-center gap-2 ${loginMode === 'kid' ? 'bg-jungle-500 text-white shadow-lg' : 'text-jungle-600'}`}
          >
            <SafeIcon icon={FiUser} /> Kid
          </button>
          <button
            onClick={() => { setLoginMode('parent'); setIdentifier(''); setPassword(''); }}
            className={`flex-1 py-3 rounded-xl font-kid transition-all flex items-center justify-center gap-2 ${loginMode === 'parent' ? 'bg-blue-500 text-white shadow-lg' : 'text-blue-600'}`}
          >
            <SafeIcon icon={FiShield} /> Parent
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <SafeIcon 
              icon={loginMode === 'kid' ? FiUser : FiMail} 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-jungle-400" 
            />
            <input
              type={loginMode === 'kid' ? "text" : "email"}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={loginMode === 'kid' ? "Explorer's Name" : "Parent Email"}
              required
              className="w-full pl-12 pr-4 py-4 bg-jungle-50 border-2 border-jungle-100 rounded-2xl focus:outline-none focus:border-jungle-400 font-comic"
            />
          </div>

          <div className="relative">
            <SafeIcon 
              icon={FiLock} 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-jungle-400" 
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="w-full pl-12 pr-4 py-4 bg-jungle-50 border-2 border-jungle-100 rounded-2xl focus:outline-none focus:border-jungle-400 font-comic"
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm font-comic text-center bg-red-50 p-3 rounded-xl border border-red-100">
              {error}
            </div>
          )}

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading}
            className={`w-full p-4 rounded-2xl font-kid text-xl shadow-lg transition-all flex items-center justify-center gap-2 ${loginMode === 'kid' ? 'bg-jungle-500 hover:bg-jungle-600' : 'bg-blue-500 hover:bg-blue-600'} text-white`}
          >
            {loading ? (
              <SafeIcon icon={FiLoader} className="animate-spin" />
            ) : (
              <>{loginMode === 'kid' ? 'Start Adventure' : 'Login'} <SafeIcon icon={FiArrowRight} /></>
            )}
          </motion.button>
        </form>

        <div className="mt-8 text-center font-comic text-jungle-600">
          New Parent? <Link to="/signup" className="text-jungle-700 font-bold underline">Create Account</Link>
        </div>
      </div>
    </motion.div>
  );
}

export default Login;